import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { formatCurrency } from '../utils/formatters';

type CombinedComparisonChartProps = {
  comparisonMetrics: {
    totalPumpdRevenue: number;
    totalUberEatsRevenue: number;
    deliveryOrderCount: number;
  };
  feeComparisonData: Array<{ name: string; pumpd: number; ubereats: number }>;
  rawData: any[];
};

const CombinedComparisonChart: React.FC<CombinedComparisonChartProps> = ({ 
  comparisonMetrics, 
  feeComparisonData,
  rawData 
}) => {
  // Calculate fees from raw data
  let pumpdCommission = 0;
  let pumpdStripeFees = 0;
  let pumpdDeliveryFees = 0;
  let uberCommission = 0;
  let uberDeliveryFees = 0;
  let pumpdTotalCostToCustomer = 0;
  let uberTotalCostToCustomer = 0;
  
  rawData.forEach(order => {
    if (order.service_type === 'delivery') {
      pumpdCommission += parseFloat(order.pumpd_commission) || 0;
      pumpdStripeFees += parseFloat(order.pumpd_stripe_fee) || 0;
      pumpdDeliveryFees += parseFloat(order.delivery_fee_on_pumpd) || 0;
      uberCommission += parseFloat(order.ubereats_commission) || 0;
      uberDeliveryFees += parseFloat(order.ubereats_total_delivery_fees) || 0;
      // Use pre-calculated total cost values that include tips
      pumpdTotalCostToCustomer += parseFloat(order.pumpd_total_cost_for_customer) || 0;
      uberTotalCostToCustomer += parseFloat(order.ubereats_cost_to_customer) || 0;
    }
  });
  
  // Prepare data for stacked bar chart
  const data = [
    {
      platform: 'UberEats',
      netRevenue: comparisonMetrics.totalUberEatsRevenue,
      commission: uberCommission,
      stripeFees: 0,
      deliveryFees: uberDeliveryFees,
    },
    {
      platform: "Pump'd",
      netRevenue: comparisonMetrics.totalPumpdRevenue,
      commission: pumpdCommission,
      stripeFees: pumpdStripeFees,
      deliveryFees: pumpdDeliveryFees,
    }
  ];
  
  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const isUberEats = label === 'UberEats';
      const total = isUberEats ? uberTotalCostToCustomer : pumpdTotalCostToCustomer;
      
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium text-sm mb-2">{label}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between gap-4 pb-2 border-b">
              <span className="text-muted-foreground">Total Cost to Customer:</span>
              <span className="font-medium">{formatCurrency(total)}</span>
            </div>
            {payload
              .filter((entry: any) => !isUberEats || entry.name !== 'Stripe Fees')
              .map((entry: any, index: number) => (
                <div key={index} className="flex justify-between gap-4">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <div 
                      className="w-3 h-3 rounded-sm" 
                      style={{ backgroundColor: entry.color }}
                    />
                    {entry.name}:
                  </span>
                  <span className="font-medium">{formatCurrency(entry.value)}</span>
                </div>
              ))}
          </div>
        </div>
      );
    }
    
    return null;
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Platform Comparison</CardTitle>
        <CardDescription>Revenue and fee breakdown by platform</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 20, right: 10, left: 40, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="platform" />
              <YAxis tickFormatter={(value) => formatCurrency(value)} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="netRevenue" stackId="a" fill="#56c873" name="Net Revenue" />
              <Bar dataKey="commission" stackId="a" fill="#ffb866" name="Commission" />
              <Bar dataKey="stripeFees" stackId="a" fill="#9691ed" name="Stripe Fees" />
              <Bar dataKey="deliveryFees" stackId="a" fill="#e54f45" name="Delivery Fees" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default CombinedComparisonChart;